import { useState, useEffect } from 'react';
import { Form, Card, Row, Col, Button, Image } from 'react-bootstrap';
import Map, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import icAdress from './assets/img/ic-home.svg';
import icCountry from './assets/img/ic-globe.svg';
import icCity from './assets/img/ic-city.svg';
import icPhone from './assets/img/ic-smart-phone.svg';
import icValidationError from './assets/img/ic-danger.svg';
import { validateURL, validateField, validateRequiredField} from './utils/validation';
import { MAPBOX_TOKEN } from './config/mapbox';

const CompanyAddAbout = ({ setShowErrorMessage, isEditing, setIsEditing, validationAttempted, formData, handleChange, errors, setErrors, handleAboutSubmit, isEditPage, showAddress2, setShowAddress2, logo: logoSrc }) => {

    const [tempFormData, setTempFormData] = useState({ ...formData }); // ✅ Store initial values
    const [suggestions1, setSuggestions1] = useState([]); // ✅ Address 1 suggestions
    const [suggestions2, setSuggestions2] = useState([]); // ✅ Address 2 suggestions
    const [showSuggestions1, setShowSuggestions1] = useState(false); // ✅ Address 1 suggestions
    const [showSuggestions2, setShowSuggestions2] = useState(false); // ✅ Address 2 suggestions
    const [touched, setTouched] = useState({});
    const [isCountryLocked, setIsCountryLocked] = useState(false);
    const [isCountry2Locked, setIsCountry2Locked] = useState(false);
    const [isCity2Locked, setIsCity2Locked] = useState(false);
    const [isCityLocked, setIsCityLocked] = useState(false);

    const [viewport, setViewport] = useState({
        latitude: formData.latitude || 47.3769, // Default to Zurich latitude
        longitude: formData.longitude || 8.5417, // Default to Zurich longitude
        zoom: 16,
    });

    const [viewport2, setViewport2] = useState({
        latitude: formData.latitude2 || 59.9133, // Default to Oslo latitude
        longitude: formData.longitude2 || 10.7388, // Default to Oslo longitude
        zoom: 16
    });

    // ✅ Enable editing mode
    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // ✅ Store previous values
    };

    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!formData.address.trim()) newErrors.address = 'Address is required.';
        if (!formData.country.trim()) newErrors.country = 'Country is required.';
        if (!formData.city.trim()) newErrors.city = 'City is required.';
        if (!formData.zip.trim()) newErrors.zip = 'ZIP Code is required.';
        // if (!formData.otherInformation.trim()) newErrors.otherInformation = 'Description is required.';

        if (Object.keys(newErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...newErrors })); // ✅ Show inline error messages
            setShowErrorMessage(true); // ✅ Show error alert
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        if (typeof handleAboutSubmit === 'function') {
            const result = await handleAboutSubmit(e);
            if (result) {
                setIsEditing(false); // ✅ Exit edit mode only if save succeeded
            }
        } else {
            console.error('Error: handleAboutSubmit is not a function.');
        }
    };


    // ✅ Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        handleChange({ target: { name: 'formData', value: tempFormData } });
        setShowSuggestions1(false);
        setShowSuggestions2(false);
    };

    // Log formData latitude and longitude on component mount
    useEffect(() => {

        if (formData.latitude && formData.longitude) {
            setViewport({
                latitude: formData.latitude,
                longitude: formData.longitude,
                zoom: viewport.zoom, // Preserve current zoom level
            });
        }
    }, [formData.latitude, formData.longitude]);

    useEffect(() => {
        console.log('Setting viewport2 - latitude2:', formData.latitude2, 'longitude2:', formData.longitude2);

        const lat = Number(formData.latitude2);
        const lng = Number(formData.longitude2);

        if (!isNaN(lat) && !isNaN(lng) && lat && lng) { // ✅ Added lat && lng check
            setViewport2((prev) => ({
                ...prev,
                latitude: lat,
                longitude: lng
            }));
        } else {
            console.warn('Invalid or missing latitude2/longitude2, using defaults');
        }
    }, [formData.latitude2, formData.longitude2]);


    // Handle childUrl field change with validation
    const handleChildUrlChange = (e) => {
        const { value } = e.target;
        handleChange(e);
        setTouched(prev => ({ ...prev, childUrl: true }));

        validateField('childUrl', value, validateURL, setErrors, 'Please enter a valid URL.');
    };


    // Handle childUrl field change with validation
    const handleChildUrl2Change = (e) => {
        const { value } = e.target;
        handleChange(e);
        setTouched(prev => ({ ...prev, childUrl2: true }));

        validateField('childUrl2', value, validateURL, setErrors, 'Please enter a valid URL.');
    };

    // ✅ Handle address input changes and fetch suggestions only in edit mode
    const handleAddressChange = async (e, isAddress2 = false) => {
        if (!isEditing) return;
        const rawValue = e.target.value;
        const address = rawValue.trim();
        const fieldName = isAddress2 ? 'address2' : 'address';

        // 🔹 1. Set value to parent
        handleChange({ target: { name: fieldName, value: address } });

        // 🔹 2. Validate required
        validateRequiredField(fieldName, address, setErrors, `${isAddress2 ? 'Address 2' : 'Address'} is required.`);

        // 🔹 3. Fetch suggestions if input is long enough
        if (address.length > 3) {
            try {
                const response = await fetch(
                    `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(address)}.json?access_token=${MAPBOX_TOKEN}&autocomplete=true&limit=10`
                );
                const data = await response.json();

                if (isAddress2) {
                    setSuggestions2(data.features || []);
                    setShowSuggestions2(true);
                } else {
                    setSuggestions1(data.features || []);
                    setShowSuggestions1(true);
                }
            } catch (error) {
                console.error('Error fetching suggestions:', error);
            }
        } else {
            if (isAddress2) {
                setSuggestions2([]);
                setShowSuggestions2(false);
            } else {
                setSuggestions1([]);
                setShowSuggestions1(false);
            }
        }
    };

    const handleCountryChange = (e) => {
        const { value } = e.target;
        handleChange(e);
        setTouched(prev => ({ ...prev, country: true }));

        validateRequiredField('country', value, setErrors, 'Country is required.');
    };

    // ✅ Handle suggestion click
    const handleSuggestionClick = (suggestion, isAddress2 = false) => {
        const { center, place_name, context } = suggestion;

        const country = context?.find((item) => item.id.startsWith('country'))?.text || '';
        const city = context?.find((item) => item.id.startsWith('place'))?.text || '';
        const zip = context?.find((item) => item.id.startsWith('postcode'))?.text || '';

        if (isAddress2) {
            handleChange({ target: { name: 'address2', value: place_name } });
            handleChange({ target: { name: 'latitude2', value: center[1] } });
            handleChange({ target: { name: 'longitude2', value: center[0] } });
            if (country) {
                handleChange({ target: { name: 'country2', value: country } });
                setIsCountry2Locked(true); // ✅ lock country2
            }
            if (city) {
                handleChange({ target: { name: 'city2', value: city } });
                setIsCity2Locked(true); // ✅ lock city2
            }
            if (zip) handleChange({ target: { name: 'zip2', value: zip } });
            setShowSuggestions2(false);
        } else {
            handleChange({ target: { name: 'address', value: place_name } });
            handleChange({ target: { name: 'latitude', value: center[1] } });
            handleChange({ target: { name: 'longitude', value: center[0] } });
            if (country) {
                handleChange({ target: { name: 'country', value: country } });
                setIsCountryLocked(true); // ✅ lock country
            }
            if (city) {
                handleChange({ target: { name: 'city', value: city } });
                setIsCityLocked(true); // ✅ lock city
            }
            if (zip) handleChange({ target: { name: 'zip', value: zip } });
            setShowSuggestions1(false);
        }

        setViewport({ ...viewport, latitude: center[1], longitude: center[0] });
    };   

     // ✅ Handle map click to set coordinates
    const handleMapClick = async (e) => {
        if (!isEditing) return;
        const { lngLat } = e;
        const latitude = lngLat.lat;
        const longitude = lngLat.lng;

        handleChange({ target: { name: 'latitude', value: latitude } });
        handleChange({ target: { name: 'longitude', value: longitude } });

        setViewport({ ...viewport, latitude, longitude });

        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}`
            );
            const data = await response.json();
            if (data.features.length > 0) {
                const place = data.features[0];
                const address = place.place_name;
                const country = place.context?.find((item) => item.id.startsWith('country'))?.text || '';
                const city = place.context?.find((item) => item.id.startsWith('place'))?.text || '';
                const zip = place.context?.find((item) => item.id.startsWith('postcode'))?.text || '';

                handleChange({ target: { name: 'address', value: address } });
                if (country) {
                    handleChange({ target: { name: 'country', value: country } });
                    setIsCountryLocked(true); // ✅ lock country on map click
                }
                if (city) {
                    handleChange({ target: { name: 'city', value: city } });
                    setIsCityLocked(true); // ✅ lock city on map click
                }
                if (zip) handleChange({ target: { name: 'zip', value: zip } });
            }
        } catch (error) {
            console.error('Error fetching address from coordinates:', error);
        }
    };

    const handleMapClick2 = async (e) => {
        if (!isEditing) return;
        const { lngLat } = e;
        const latitude = lngLat.lat;
        const longitude = lngLat.lng;

        handleChange({ target: { name: 'latitude2', value: latitude } });
        handleChange({ target: { name: 'longitude2', value: longitude } });

        setViewport2({ ...viewport2, latitude, longitude });

        try {
            const response = await fetch(
                `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}`
            );
            const data = await response.json();
            if (data.features.length > 0) {
                const place = data.features[0];
                const address = place.place_name;
                const country = place.context?.find((item) => item.id.startsWith('country'))?.text || '';
                const city = place.context?.find((item) => item.id.startsWith('place'))?.text || '';
                const zip = place.context?.find((item) => item.id.startsWith('postcode'))?.text || '';

                handleChange({ target: { name: 'address2', value: address } });
                if (country) {
                    handleChange({ target: { name: 'country2', value: country } });
                    setIsCountry2Locked(true); // ✅ lock country2 on map click
                }
                if (city) {
                    handleChange({ target: { name: 'city2', value: city } });
                    setIsCity2Locked(true); // ✅ lock city2 on map click
                }
                if (zip) handleChange({ target: { name: 'zip2', value: zip } });
            }
        } catch (error) {
            console.error('Error fetching address2 from coordinates:', error);
        }
    };



    return (
        <>
            <Card>
                <Card.Body>
                    <Row>
                        <Col md={12}>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">1.</span> About Company</h3>
                        </Col>
                    </Row>
                    <Row className="map-row mb-4">
                        <Col md={12} className="p-0">
                            
                            {/* Map with draggable and zoom enabled only in edit mode */}
                            {isEditPage && (
                                <>
                                    <Map
                                        {...viewport}
                                        mapStyle="mapbox://styles/mapbox/light-v10"
                                        mapboxAccessToken={MAPBOX_TOKEN}
                                        dragPan={isEditing} // ✅ Enable dragging only when editing
                                        scrollZoom={isEditing} // ✅ Allow zooming only when editing
                                        doubleClickZoom={isEditing}
                                        touchZoomRotate={isEditing}
                                        onMove={(evt) => setViewport(evt.viewState)}
                                        onClick={handleMapClick} // ✅ Allow clicking to set location
                                    >
                                        <Marker
                                            latitude={formData.latitude || viewport.latitude}
                                            longitude={formData.longitude || viewport.longitude}
                                            draggable={isEditing} // ✅ Enable dragging only in edit mode
                                            onDragEnd={async (e) => {
                                                const lat = e.lngLat.lat;
                                                const lng = e.lngLat.lng;

                                                handleChange({ target: { name: 'latitude', value: lat } });
                                                handleChange({ target: { name: 'longitude', value: lng } });
                                                setViewport({ ...viewport, latitude: lat, longitude: lng });

                                                try {
                                                    const response = await fetch(
                                                        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`
                                                    );
                                                    const data = await response.json();
                                                    if (data.features.length > 0) {
                                                        const place = data.features[0];
                                                        const address = place.place_name;
                                                        const country = place.context?.find((item) => item.id.startsWith('country'))?.text || '';
                                                        const city = place.context?.find((item) => item.id.startsWith('place'))?.text || '';
                                                        const zip = place.context?.find((item) => item.id.startsWith('postcode'))?.text || '';

                                                        handleChange({ target: { name: 'address', value: address } });
                                                        if (country) handleChange({ target: { name: 'country', value: country } });
                                                        if (city) handleChange({ target: { name: 'city', value: city } });
                                                        if (zip) handleChange({ target: { name: 'zip', value: zip } });
                                                    }
                                                } catch (error) {
                                                    console.error('Error fetching address from coordinates:', error);
                                                }
                                            }}

                                        >
                                            <div className="marker-bg">
                                                <img
                                                    src={logoSrc || 'https://docs.mapbox.com/help/demos/custom-markers-gl-js/mapbox-icon.png'}
                                                    alt={formData.title}
                                                />
                                            </div>
                                        </Marker>
                                    </Map>
                                </>
                            )}

                            {!isEditPage && (
                                <>
                                    <Map
                                        {...viewport}
                                        mapStyle="mapbox://styles/mapbox/light-v10"
                                        mapboxAccessToken={MAPBOX_TOKEN}
                                        dragPan={isEditing} // ✅ Enable dragging only when editing
                                        scrollZoom={isEditing} // ✅ Allow zooming only when editing
                                        doubleClickZoom={isEditing}
                                        touchZoomRotate={isEditing}
                                        onMove={(evt) => setViewport(evt.viewState)}
                                        onClick={handleMapClick} // ✅ Allow clicking to set location
                                    >
                                        <Marker
                                            latitude={formData.latitude || viewport.latitude}
                                            longitude={formData.longitude || viewport.longitude}
                                            draggable={isEditing} // ✅ Enable dragging only in edit mode
                                            onDragEnd={async (e) => {
                                                const lat = e.lngLat.lat;
                                                const lng = e.lngLat.lng;

                                                handleChange({ target: { name: 'latitude', value: lat } });
                                                handleChange({ target: { name: 'longitude', value: lng } });
                                                setViewport({ ...viewport, latitude: lat, longitude: lng });

                                                try {
                                                    const response = await fetch(
                                                        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`
                                                    );
                                                    const data = await response.json();
                                                    if (data.features.length > 0) {
                                                        const place = data.features[0];
                                                        const address = place.place_name;
                                                        const country = place.context?.find((item) => item.id.startsWith('country'))?.text || '';
                                                        const city = place.context?.find((item) => item.id.startsWith('place'))?.text || '';
                                                        const zip = place.context?.find((item) => item.id.startsWith('postcode'))?.text || '';

                                                        handleChange({ target: { name: 'address', value: address } });
                                                        if (country) handleChange({ target: { name: 'country', value: country } });
                                                        if (city) handleChange({ target: { name: 'city', value: city } });
                                                        if (zip) handleChange({ target: { name: 'zip', value: zip } });
                                                    }
                                                } catch (error) {
                                                    console.error('Error fetching address from coordinates:', error);
                                                }
                                            }}
                                        >   
                                            <div className="marker-bg">
                                                <img
                                                    src={logoSrc || 'https://docs.mapbox.com/help/demos/custom-markers-gl-js/mapbox-icon.png'}
                                                    alt={formData.title}
                                                />
                                            </div>
                                        </Marker>
                                    </Map>
                                </>
                            )}

                        </Col>
                    </Row>
                    {isEditing ? (
                        <>  
                            <Row className="my-4">
                                <Col><h4 className="h3 my-0">Address</h4></Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={12}>
                                    {/* Address Input with Suggestions */}
                                    <Form.Group className="validated" controlId="address">
                                        <Form.Label className="text-start">Address <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="address"
                                            value={formData.address}
                                            onChange={(e) => handleAddressChange(e, false)} // ✅ just updates value and suggestions
                                            onBlur={() => {
                                                setTouched((prev) => ({ ...prev, address: true }));
                                                if (!formData.address.trim()) {
                                                setErrors((prev) => ({ ...prev, address: 'Address is required.' }));
                                                } else {
                                                setErrors((prev) => {
                                                    const updated = { ...prev };
                                                    delete updated.address;
                                                    return updated;
                                                });
                                                }
                                                console.log("Address blurred"); // ✅ keep for debug
                                            }}
                                            isInvalid={(!!errors.address && validationAttempted) || (touched.address && !!errors.address)}
                                            required
                                            disabled={!isEditing}
                                        />
                                        {errors.address && (
                                            console.log('Showing address error'),
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.address} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.address}
                                                </div>
                                            </div>
                                        )}
                                        {showSuggestions1 && suggestions1.length > 0 && (
                                            <div className="suggestions">
                                                {suggestions1.map((suggestion) => (
                                                    <div
                                                        key={suggestion.id}
                                                        className="suggestion-item"
                                                        onClick={() => handleSuggestionClick(suggestion, false)} // ✅ Address 1
                                                    >
                                                        {suggestion.place_name}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}> 
                                    {/* Country */}
                                    <Form.Group className="validated" controlId="country">
                                        <Form.Label>Country <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="country"
                                            value={formData.country}
                                            readOnly={isCountryLocked} // ✅ makes input un-editable if locked
                                            onChange={handleCountryChange} // ✅ use local validation
                                            onBlur={() => {
                                                setTouched(prev => ({ ...prev, country: true }));

                                                if (!formData.country.trim()) {
                                                    setErrors(prev => ({ ...prev, country: 'Country is required.' }));
                                                } else {
                                                    setErrors(prev => {
                                                    const updated = { ...prev };
                                                    delete updated.country;
                                                    return updated;
                                                    });
                                                }
                                            }}
                                            isInvalid={(!!errors.country && validationAttempted) || (touched.country && !!errors.country)}
                                            required
                                            disabled={!isEditing}
                                        />
                                        {/* ✅ Custom error icon + tooltip */}
                                        {errors.country && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.country} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.country}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}> 
                                    {/* City */}
                                    <Form.Group className="validated" controlId="city">
                                        <Form.Label>City <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="city"
                                            value={formData.city}
                                            readOnly={isCityLocked} // ✅ makes input un-editable if locked
                                            onChange={handleChange}
                                            onBlur={() => {
                                                setTouched((prev) => ({ ...prev, city: true }));
                                                if (!formData.city.trim()) {
                                                    setErrors((prev) => ({ ...prev, city: 'City is required.' }));
                                                }
                                            }}
                                            isInvalid={(!!errors.city && validationAttempted) || (touched.city && !!errors.city)}
                                            required
                                            disabled={!isEditing}
                                        />
                                        
                                        {errors.city && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.city} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.city}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={4}>
                                    {/* City Region */}
                                    <Form.Group controlId="cityRegion">
                                        <Form.Label>City Region</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="cityRegion"
                                            value={formData.cityRegion || ''}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    {/* ZIP Code */}
                                    <Form.Group className="validated" controlId="zip">
                                        <Form.Label>ZIP Code <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="zip"
                                            value={formData.zip}
                                            onChange={handleChange}
                                            onBlur={() => {
                                                setTouched((prev) => ({ ...prev, zip: true }));
                                                if (!formData.zip.trim()) {
                                                    setErrors((prev) => ({ ...prev, zip: 'ZIP Code is required.' }));
                                                }
                                            }}
                                            isInvalid={(!!errors.zip && validationAttempted) || (touched.zip && !!errors.zip)}
                                            required
                                            disabled={!isEditing}
                                        />
                                        {errors.zip && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.zip} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.zip}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={4}>
                                    {/* Phone */}
                                    <Form.Group controlId="phone">
                                        <Form.Label>Phone</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            disabled={!isEditing}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={12}>
                                    {/* Other Information */}
                                    <Form.Group className="validated" controlId="otherInformation">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="otherInformation"
                                            value={formData.otherInformation}
                                            onChange={handleChange}
                                            // onBlur={() => {
                                            //     setTouched((prev) => ({ ...prev, otherInformation: true }));
                                            //     if (!formData.otherInformation.trim()) {
                                            //         setErrors((prev) => ({ ...prev, otherInformation: 'Description is required.' }));
                                            //     }
                                            // }}
                                            // isInvalid={(!!errors.otherInformation)}
                                            // required
                                            disabled={!isEditing}
                                        />
                                        {errors.otherInformation && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.otherInformation} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.otherInformation}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={12}>
                                    {/* Url */}
                                    <Form.Group className="validated" controlId="childUrl">
                                        <Form.Label>URL</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="childUrl"
                                            value={formData.childUrl}
                                            onChange={handleChildUrlChange}
                                            isInvalid={(!!errors.childUrl && validationAttempted) || (touched.childUrl && !!errors.childUrl)}
                                            disabled={!isEditing} // ✅ Allow typing in edit mode
                                        />
                                        {errors.childUrl && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.childUrl} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.childUrl}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>

                    ) : (
                        // Not editing
                        <>
                            <Row className="mb-4">
                                <Col md={12}>
                                    {/* Address Input with Suggestions */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icAdress}
                                                alt="Address"
                                            />
                                        </span>
                                        <span className="text">
                                            Address:
                                            <br />
                                            <strong>{formData.address?.split(',')[0]?.trim()}</strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={4}> 
                                    {/* Country */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icCountry}
                                                alt="Country"
                                            />
                                        </span>
                                        <span className="text">
                                            Country:
                                            <br />
                                            <strong>{formData.country}</strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4}> 
                                    {/* City */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icCity}
                                                alt="City"
                                            />
                                        </span>
                                        <span className="text">
                                            City:
                                            <br />
                                            <strong>{formData.city}</strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4}> 
                                    {/* City Region */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icCity}
                                                alt="City region"
                                            />
                                        </span>
                                        <span className="text">
                                            City Region:
                                            <br />
                                            <strong>{formData.cityRegion || 'N/A'}</strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={4}>
                                    {/* ZIP Code */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icCity}
                                                alt="Zip code"
                                            />
                                        </span>
                                        <span className="text">
                                            Zip code:
                                            <br />
                                            <strong>{formData.zip}</strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    {/* Phone */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icPhone}
                                                alt="Phone"
                                            />
                                        </span>
                                        <span className="text">
                                            Phone:
                                            <br />
                                            <strong>{formData.phone || "N/A"}</strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </>
                    )}
                    {/* "Add Address" Button and Address #2 Field */}
                    {isEditing && !showAddress2 ? (
                        <Row>
                            <Col md={12} className="d-flex justify-content-center">
                                <Button variant="outline-secondary" className="btn-sm" onClick={() => setShowAddress2(true)}>
                                    Add Address
                                </Button>
                            </Col>
                        </Row>
                    ) : isEditing && showAddress2 ? (
                        <>
                            <Row className="map-row mb-4">
                                <Col md={12} className="p-0">
                                    <Map
                                        {...viewport2}
                                        mapStyle="mapbox://styles/mapbox/light-v10"
                                        mapboxAccessToken={MAPBOX_TOKEN}
                                        style={{ width: '100%', height: 400 }} // ✅ added height
                                        dragPan={isEditing}
                                        scrollZoom={isEditing}
                                        doubleClickZoom={isEditing}
                                        touchZoomRotate={isEditing}
                                        onMove={(evt) => setViewport2(evt.viewState)}
                                        onClick={handleMapClick2} // ✅ Allow clicking to set location
                                    >
                                        <Marker
                                            latitude={formData.latitude2 || viewport2.latitude}
                                            longitude={formData.longitude2 || viewport2.longitude}
                                            draggable={isEditing}
                                            onDragEnd={async (e) => {
                                                const lat = e.lngLat.lat;
                                                const lng = e.lngLat.lng;

                                                handleChange({ target: { name: 'latitude2', value: lat } });
                                                handleChange({ target: { name: 'longitude2', value: lng } });
                                                setViewport2({ ...viewport2, latitude: lat, longitude: lng });

                                                try {
                                                    const response = await fetch(
                                                        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`
                                                    );
                                                    const data = await response.json();
                                                    if (data.features.length > 0) {
                                                        const place = data.features[0];
                                                        const address = place.place_name;
                                                        const country = place.context?.find((item) => item.id.startsWith('country'))?.text || '';
                                                        const city = place.context?.find((item) => item.id.startsWith('place'))?.text || '';
                                                        const zip = place.context?.find((item) => item.id.startsWith('postcode'))?.text || '';

                                                        handleChange({ target: { name: 'address2', value: address } });
                                                        if (country) handleChange({ target: { name: 'country2', value: country } });
                                                        if (city) handleChange({ target: { name: 'city2', value: city } });
                                                        if (zip) handleChange({ target: { name: 'zip2', value: zip } });
                                                    }
                                                } catch (error) {
                                                    console.error('Error fetching address2 from coordinates:', error);
                                                }
                                            }}
                                        >
                                            <div className="marker-bg">
                                                <img
                                                    src={logoSrc || 'https://docs.mapbox.com/help/demos/custom-markers-gl-js/mapbox-icon.png'}
                                                    alt={formData.title}
                                                />
                                            </div>
                                        </Marker>
                                    </Map>
                                </Col>
                            </Row>

                            <Row className="my-4">
                                <Col><h4 className="h3 my-0">Address 2</h4></Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={12}>        
                                    <Form.Group className="validated" controlId="address2">
                                        <Form.Label>Address <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="address2"
                                            value={formData.address2 || ''}
                                            onChange={(e) => handleAddressChange(e, true)} // ✅ Pass true for Address 2
                                            disabled={!isEditing}
                                            isInvalid={!!errors.address2}
                                        />
                                        {/* Address 2 Suggestions */}
                                        {showSuggestions2 && showAddress2 && suggestions2.length > 0 && (
                                            <div className="suggestions">
                                                {suggestions2.map((suggestion) => (
                                                    <div
                                                        key={suggestion.id}
                                                        className="suggestion-item"
                                                        onClick={() => handleSuggestionClick(suggestion, true)} // ✅ Address 2
                                                    >
                                                        {suggestion.place_name}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        {errors.address2 && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.address2} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.address2}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}> 
                                    {/* Country 2 */}
                                    <Form.Group className="validated" controlId="country2">
                                        <Form.Label>Country <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="country2"
                                            value={formData.country2}
                                            readOnly={isCountry2Locked}
                                            onChange={handleChange}
                                            isInvalid={!!errors.country2}
                                            disabled={!isEditing} // ✅ Disable input when not in edit mode
                                        />
                                        {errors.country2 && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.country2} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.country2}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}> 
                                    {/* City */}
                                    <Form.Group className="validated" controlId="city2">
                                        <Form.Label>City <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="city2"
                                            value={formData.city2}
                                            readOnly={isCity2Locked}
                                            onChange={handleChange}
                                            isInvalid={!!errors.city2}
                                            disabled={!isEditing} // ✅ Disable input when not in edit mode
                                        />
                                        {errors.city2 && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.city2} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.city2}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    {/* ZIP Code */}
                                    <Form.Group className="validated" controlId="zip2">
                                        <Form.Label>ZIP Code <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="zip2"
                                            value={formData.zip2}
                                            onChange={handleChange}
                                            isInvalid={!!errors.zip2}
                                            disabled={!isEditing} // ✅ Disable input when not in edit mode
                                        />
                                        {errors.zip2 && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.zip2} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.zip2}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    {/* Phone */}
                                    <Form.Group controlId="phone2">
                                        <Form.Label>Phone</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="phone2"
                                            value={formData.phone2}
                                            onChange={handleChange}
                                            disabled={!isEditing} // ✅ Disable input when not in edit mode
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={12}>
                                    {/* Other Information */}
                                    <Form.Group className="validated" controlId="otherInformation2">
                                        <Form.Label>Description <span>*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="otherInformation2"
                                            value={formData.otherInformation2}
                                            onChange={handleChange}
                                            isInvalid={!!errors.otherInformation2}
                                            disabled={!isEditing} // ✅ Disable input when not in edit mode
                                        />
                                        {errors.otherInformation2 && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.otherInformation2} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.otherInformation2}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={12}>
                                    {/* Other Information */}
                                    <Form.Group className="validated" controlId="childUrl2">
                                        <Form.Label>URL</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="childUrl2"
                                            value={formData.childUrl2}
                                            onChange={handleChildUrl2Change}
                                            isInvalid={!!errors.childUrl2}
                                            disabled={!isEditing} // ✅ Allow typing in edit mode
                                        />
                                        {errors.childUrl2 && (
                                            <div className="custom-error-icon-wrapper">
                                                <img src={icValidationError} alt={errors.childUrl2} className="custom-error-icon" />
                                                <div className="custom-tooltip">
                                                    {errors.childUrl2}
                                                </div>
                                            </div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>
                             <Row className="mt-4">
                                <Col md={12} className="d-flex justify-content-center">
                                    <Button
                                        variant="outline-danger"
                                        className="btn-sm"
                                        onClick={() => {
                                            setShowAddress2(false);
                                            handleChange({ target: { name: 'address2', value: '' } });
                                            handleChange({ target: { name: 'country2', value: '' } });
                                            handleChange({ target: { name: 'city2', value: '' } });
                                            handleChange({ target: { name: 'zip2', value: '' } });
                                            handleChange({ target: { name: 'phone2', value: '' } });
                                            handleChange({ target: { name: 'latitude2', value: '' } });
                                            handleChange({ target: { name: 'longitude2', value: '' } });
                                            handleChange({ target: { name: 'otherInformation2', value: '' } });
                                            handleChange({ target: { name: 'childUrl2', value: '' } });
                                        }}
                                    >
                                        Remove Address #2
                                    </Button> {/* ✅ Button to remove Address #2 */}
                                </Col>
                            </Row>
                        </>
                    ) : formData.address2 ? (
                        <>  
                            <Row className="mt-5 mb-4">
                                <Col><h4 className="h3 my-0">Address 2</h4></Col>
                            </Row>
                            <Row className="map-row mb-4">
                                <Col md={12} className="p-0">
                                    <Map
                                        {...viewport2}
                                        mapStyle="mapbox://styles/mapbox/light-v10"
                                        mapboxAccessToken={MAPBOX_TOKEN}
                                        style={{ width: '100%', height: 400 }} // ✅ added height
                                        dragPan={isEditing}
                                        scrollZoom={isEditing}
                                        doubleClickZoom={isEditing}
                                        touchZoomRotate={isEditing}
                                        onMove={(evt) => setViewport2(evt.viewState)}
                                        onClick={handleMapClick2} // ✅ Allow clicking to set location
                                    >
                                        <Marker
                                            latitude={formData.latitude2 || viewport2.latitude}
                                            longitude={formData.longitude2 || viewport2.longitude}
                                            draggable={isEditing}
                                            onDragEnd={async (e) => {
                                                const lat = e.lngLat.lat;
                                                const lng = e.lngLat.lng;

                                                handleChange({ target: { name: 'latitude2', value: lat } });
                                                handleChange({ target: { name: 'longitude2', value: lng } });
                                                setViewport2({ ...viewport2, latitude: lat, longitude: lng });

                                                try {
                                                    const response = await fetch(
                                                        `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${MAPBOX_TOKEN}`
                                                    );
                                                    const data = await response.json();
                                                    if (data.features.length > 0) {
                                                        const place = data.features[0];
                                                        const address = place.place_name;
                                                        const country = place.context?.find((item) => item.id.startsWith('country'))?.text || '';
                                                        const city = place.context?.find((item) => item.id.startsWith('place'))?.text || '';
                                                        const zip = place.context?.find((item) => item.id.startsWith('postcode'))?.text || '';

                                                        handleChange({ target: { name: 'address2', value: address } });
                                                        if (country) handleChange({ target: { name: 'country2', value: country } });
                                                        if (city) handleChange({ target: { name: 'city2', value: city } });
                                                        if (zip) handleChange({ target: { name: 'zip2', value: zip } });
                                                    }
                                                } catch (error) {
                                                    console.error('Error fetching address2 from coordinates:', error);
                                                }
                                            }}
                                        >
                                            <div className="marker-bg">
                                                <img
                                                    src={logoSrc || 'https://docs.mapbox.com/help/demos/custom-markers-gl-js/mapbox-icon.png'}
                                                    alt={formData.title}
                                                />
                                            </div>
                                        </Marker>
                                    </Map>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={12}>
                                    {/* Address Input with Suggestions */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icAdress}
                                                alt="Address"
                                            />
                                        </span>
                                        <span className="text">
                                            Address:
                                            <br />
                                            <strong>{formData.address2?.split(',')[0]?.trim()}</strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-4">
                                <Col md={6}> 
                                    {/* Country */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icCountry}
                                                alt="Country"
                                            />
                                        </span>
                                        <span className="text">
                                            Country:
                                            <br />
                                            <strong>{formData.country2}</strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}> 
                                    {/* City */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icCity}
                                                alt="City"
                                            />
                                        </span>
                                        <span className="text">
                                            City:
                                            <br />
                                            <strong>{formData.city2}</strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    {/* ZIP Code */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icCity}
                                                alt="Zip code"
                                            />
                                        </span>
                                        <span className="text">
                                            Zip code:
                                            <br />
                                            <strong>{formData.zip2}</strong>
                                        </span>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    {/* Phone */}
                                    <div className="icon-text">
                                        <span className="icon" aria-hidden="true">
                                            <Image
                                                src={icPhone}
                                                alt="Phone"
                                            />
                                        </span>
                                        <span className="text">
                                            Phone:
                                            <br />
                                            <strong>{formData.phone2 || "N/A"}</strong>
                                        </span>
                                    </div>
                                </Col>
                            </Row>
                        </>

                    ) : null}
                </Card.Body>
            </Card>
        </>
    );
};

export default CompanyAddAbout;
